import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { ArrowLeft, Tag, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface ShippingInfo {
  firstName: string;
  lastName: string;
  company?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  deliveryInstructions?: string;
}

interface Discount {
  code: string;
  discount_percentage: number | null;
  fixed_discount: number | null;
  expiry_date: string;
  status: 'active' | 'expired';
}

const CheckoutForm: React.FC = () => {
  const { cartItems, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Discount | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [shippingInfo] = useState<ShippingInfo>({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: user?.email || '',
    deliveryInstructions: '',
  });

  const validateCoupon = async (code: string): Promise<Discount | null> => {
    try {
      const { data: coupon, error } = await supabase
        .from('discounts')
        .select('code, discount_percentage, fixed_discount, expiry_date, status')
        .eq('code', code)
        .single();

      if (error || !coupon) {
        setCouponError('Invalid coupon code');
        return null;
      }

      if (coupon.status !== 'active') {
        setCouponError('This coupon is not active');
        return null;
      }

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const expiryDate = new Date(coupon.expiry_date);
      expiryDate.setHours(0, 0, 0, 0);

      if (currentDate > expiryDate) {
        setCouponError('Coupon has expired');
        return null;
      }

      setCouponError(null);
      return coupon as Discount;
    } catch (error: any) {
      console.error('Error validating coupon:', error);
      setCouponError('Failed to validate coupon');
      return null;
    }
  };

  const applyCoupon = async () => {
    if (!couponCode) {
      setCouponError('Please enter a coupon code');
      return;
    }

    const coupon = await validateCoupon(couponCode);
    if (coupon) {
      setAppliedCoupon(coupon);
      toast.success('Coupon applied successfully!');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
    toast.success('Coupon removed');
  };

  const calculateDiscountedTotal = () => {
    if (!appliedCoupon) return total;

    let discountedTotal = total;
    if (appliedCoupon.discount_percentage !== null) {
      discountedTotal -= (total * appliedCoupon.discount_percentage) / 100;
    } else if (appliedCoupon.fixed_discount !== null) {
      discountedTotal -= appliedCoupon.fixed_discount;
    }
    return Math.max(0, discountedTotal);
  };

  const discountedTotal = calculateDiscountedTotal();
  const discountAmount = total - discountedTotal;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.error('Online ordering is temporarily disabled. We are still taking Party & Takeaway Orders via phone: 07521 262119');
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="relative flex items-center justify-between mb-6 sm:mb-10">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            <span className="text-sm sm:text-base lg:text-lg font-semibold">Back to Cart</span>
          </button>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-indigo-900 dark:text-white absolute right-0">
            Checkout
          </h1>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10 px-2 py-6 lg:px-0 lg:py-0">
          {/* Order Summary */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 border border-teal-100 dark:border-gray-700">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-indigo-900 dark:text-teal-300 mb-4 sm:mb-6 lg:mb-8">
              Order Summary
            </h2>
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Dishes Selected */}
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between bg-teal-50 dark:bg-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-3 sm:space-x-5 lg:space-x-7">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-cover rounded-lg border border-teal-200 dark:border-gray-600"
                      onError={(e) => (e.currentTarget.src = '/placeholder-image.jpg')}
                    />
                    <div>
                      <span className="text-indigo-900 dark:text-teal-200 text-base sm:text-lg lg:text-xl font-semibold">
                        {item.name}
                      </span>
                      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <span className="text-indigo-900 dark:text-white font-semibold text-base sm:text-lg lg:text-xl">
                    £{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}

              {/* Apply Coupon Section */}
              <div className="border-t border-teal-200 dark:border-gray-600 pt-4 sm:pt-6 lg:pt-8">
                <div className="mb-4 sm:mb-6 lg:mb-8">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-indigo-900 dark:text-teal-300 mb-3 lg:mb-4">
                    Apply Coupon
                  </h3>
                  {appliedCoupon ? (
                    <div className="flex items-center bg-teal-100 dark:bg-teal-900 rounded-lg px-4 py-2 w-fit shadow-sm">
                      <Tag className="w-5 h-5 mr-2 text-teal-600 dark:text-teal-400" />
                      <span className="text-base sm:text-lg font-medium text-indigo-900 dark:text-white">
                        {appliedCoupon.code}
                      </span>
                      <button
                        onClick={removeCoupon}
                        className="ml-3 text-teal-600 dark:text-teal-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value);
                          setCouponError(null);
                        }}
                        placeholder="Enter coupon code"
                        className="flex-1 p-3 sm:p-4 rounded-lg bg-teal-50 dark:bg-gray-700 text-indigo-900 dark:text-white border border-teal-300 dark:border-gray-600 focus:ring-2 focus:ring-teal-500 text-sm sm:text-base shadow-sm"
                      />
                      <button
                        onClick={applyCoupon}
                        className="px-4 sm:px-5 py-3 sm:py-4 bg-gradient-to-r from-teal-500 to-teal-700 text-white rounded-lg hover:from-teal-600 hover:to-teal-800 transition-all text-sm sm:text-base shadow-md"
                      >
                        Apply Coupon
                      </button>
                    </div>
                  )}
                  {couponError && !appliedCoupon && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-2">
                      {couponError}
                    </p>
                  )}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-teal-200 dark:border-gray-600 pt-4 sm:pt-6 lg:pt-8">
                <div className="flex justify-between text-gray-700 dark:text-gray-300 text-base sm:text-lg lg:text-xl">
                  <span className="font-semibold text-indigo-900 dark:text-teal-300">Subtotal</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-gray-700 dark:text-gray-300 mt-2 sm:mt-3 lg:mt-4 text-base sm:text-lg lg:text-xl">
                    <span className="font-semibold text-indigo-900 dark:text-teal-300">Discount ({appliedCoupon.code})</span>
                    <span>-£{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg sm:text-xl lg:text-2xl font-bold text-indigo-900 dark:text-white mt-2 sm:mt-3 lg:mt-4">
                  <span>Total</span>
                  <span>£{discountedTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mt-6 sm:mt-8 lg:mt-10">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-indigo-900 dark:text-teal-300 mb-3 lg:mb-4">
                Payment Method
              </h3>
              <div className="bg-gradient-to-r from-teal-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-lg p-4 sm:p-5 lg:p-6 border border-teal-300 dark:border-gray-600 shadow-md">
                <p className="text-indigo-900 dark:text-teal-200 text-base sm:text-lg lg:text-xl font-semibold">
                  Cash on Delivery (COD)
                </p>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 lg:mt-2">
                  Pay with cash when your order is delivered.
                </p>
              </div>
            </div>
          </div>

          {/* Place Order Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 lg:sticky lg:top-10 border border-teal-100 dark:border-gray-700">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-indigo-900 dark:text-teal-300 mb-4 sm:mb-6 lg:mb-8">
              Complete Your Order
            </h3>
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-400">
                Your order will be processed and delivered to the address linked to your account.
              </p>
              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4 rounded-xl mb-6">
                <p className="text-emerald-800 dark:text-emerald-400 text-sm font-medium text-center">
                  Online ordering is temporarily disabled due to relocation.
                  <br /> We are still taking <span className="font-bold">Party</span> & <span className="font-bold">Takeaway Orders</span> via phone.
                  <br /> Please call <a href="tel:07521262119" className="font-bold underline">07521 262119</a>.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full bg-gray-400 text-white py-3 sm:py-4 lg:py-5 rounded-lg text-base sm:text-lg lg:text-xl font-semibold shadow-lg"
              >
                Checkout Disabled
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;